import React, { useState, useEffect, useCallback } from 'react';
import { C } from './tokens.js';
import { D } from './mock-data.js';
import { DEFAULT_LOCATION, findCity } from './lib/cities.js';
import { getSession } from './lib/auth.js';
import { BNav, Toggle, RoutineBasketPill } from './components/ui.jsx';
import { OfflineBanner, NotFoundScreen, SupplierLockBanner, InviteUsedScreen, InstallPwaModal, canShowInstallPrompt } from './components/system.jsx';
import { resolveSupplierInvite, completeSupplierInvite, seedMockInvites } from './api/supplierInvites.js';

import { OnboardingScreen } from './screens/OnboardingScreen.jsx';
import { PhoneScreen, OTPScreen, LocationScreen } from './screens/AuthScreens.jsx';
import { HomeScreen, CategoryFeedScreen } from './screens/HomeScreen.jsx';
import { BranchScreen, ProductDetailScreen, WriteReviewScreen } from './screens/StoreScreens.jsx';
import { CartScreen, CheckoutScreen, UPIScreen, OrderConfirmScreen } from './screens/CheckoutScreens.jsx';
import { BookingServiceScreen, BookingFlowScreen } from './screens/BookingScreens.jsx';
import { OrdersScreen, OrderDetailScreen, BookingDetailScreen } from './screens/OrderScreens.jsx';
import { SubscriptionsScreen, SubDetailScreen } from './screens/SubscriptionScreens.jsx';
import { AccountScreen, AddressEditorScreen } from './screens/AccountScreens.jsx';
import { NotificationsScreen } from './screens/NotificationsScreen.jsx';
import { SearchScreen } from './screens/SearchScreen.jsx';
import { VendorDashboard } from './screens/VendorDashboard.jsx';
import { ProductCatalogScreen } from './screens/ProductCatalogScreen.jsx';
import { RoutineReviewScreen } from './screens/RoutineReviewScreen.jsx';

const BASKET_KEY = 'rekraft.routineBasket';

const loadBasket = () => {
  try {
    const raw = localStorage.getItem(BASKET_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.items) ? parsed : { items: [] };
  } catch {
    return { items: [] };
  }
};

const saveBasket = (b) => {
  try { localStorage.setItem(BASKET_KEY, JSON.stringify(b)); } catch {}
};

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{ startAtHome: false } /*EDITMODE-END*/;

export const App = () => {
  const [screen, setScreen] = useState(TWEAK_DEFAULTS.startAtHome ? 'app' : 'onboarding');
  const [phone, setPhone] = useState('');
  const [activeTab, setActiveTab] = useState('home');
  const [stack, setStack] = useState([]);
  const [cart, setCart] = useState({ branchId: null, branchName: '', items: [] });
  const [subscriptions, setSubscriptions] = useState([...D.subscriptions]);
  const [addresses, setAddresses] = useState([...D.addresses]);
  const [location, setLocation] = useState(DEFAULT_LOCATION);
  const [showTweaks, setShowTweaks] = useState(false);
  const [routineBasket, setRoutineBasket] = useState(loadBasket);
  const [supplierLock, setSupplierLock] = useState(null);
  const [inviteSpent, setInviteSpent] = useState(null);
  const [showLockInfo, setShowLockInfo] = useState(false);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => { saveBasket(routineBasket); }, [routineBasket]);

  // After login, offer to install the PWA. Wait a tick for the browser's
  // `beforeinstallprompt` event to arrive, and re-check if it fires later.
  useEffect(() => {
    if (screen !== 'app') return;
    let cancelled = false;
    const check = () => { if (!cancelled && canShowInstallPrompt()) setShowInstall(true); };
    const t = setTimeout(check, 1500);
    window.addEventListener('beforeinstallprompt', check);
    return () => { cancelled = true; clearTimeout(t); window.removeEventListener('beforeinstallprompt', check); };
  }, [screen]);

  // Seed mock invites once so VendorDashboard analytics has data on first boot.
  useEffect(() => { seedMockInvites(); }, []);

  // Parse ?invite=<token> on first mount. If valid + unused, jump into the
  // supplier-locked flow; if already consumed, show the InviteUsedScreen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('invite');
    if (!token) return;
    let cancelled = false;
    resolveSupplierInvite(token).then((invite) => {
      if (cancelled || !invite) return;
      if (invite.status === 'paid' || invite.status === 'cancelled' || invite.status === 'expired') {
        setInviteSpent({ branchName: invite.branchName, status: invite.status });
        return;
      }
      const sess = getSession();
      const phoneMatch = sess?.phone === invite.phone;
      setPhone(invite.phone);
      setSupplierLock({
        token: invite.token,
        branchId: invite.branchId,
        branchName: invite.branchName,
        target: invite.target,
        customerName: invite.customerName,
      });
      setScreen(phoneMatch ? 'app' : 'otp');
    });
    return () => { cancelled = true; };
  }, []);

  const addToRoutineBasket = (product, branch, opts = {}) => {
    const variantLabel = opts.variantLabel || product.variants?.[0]?.l || '';
    const pricePaise = (product.variants?.find((v) => v.l === variantLabel) || product.variants?.[0])?.p || 0;
    const cadence = opts.cadence || product.cadences?.[0] || 'Daily';
    const qty = opts.qty || 1;
    setRoutineBasket((b) => {
      const items = [...b.items];
      const i = items.findIndex((it) => it.productId === product.id && it.variantLabel === variantLabel);
      if (i >= 0) {
        items[i] = { ...items[i], qty: items[i].qty + qty };
      } else {
        items.push({
          productId: product.id,
          branchId: branch.id,
          branchName: branch.name,
          branchRating: branch.rating,
          name: product.name,
          em: product.em,
          cat: product.cat,
          variantLabel,
          pricePaise,
          qty,
          cadence,
          customDays: opts.customDays || null,
          weeklyDay: opts.weeklyDay || null,
          cadences: product.cadences || ['Daily', 'Alt-day', 'Weekly', 'Custom'],
        });
      }
      return { items };
    });
  };

  const updateBasketItem = (productId, variantLabel, patch) => {
    setRoutineBasket((b) => ({
      items: b.items.map((it) => (it.productId === productId && it.variantLabel === variantLabel) ? { ...it, ...patch } : it),
    }));
  };

  const removeFromRoutineBasket = (productId, variantLabel) => {
    setRoutineBasket((b) => ({ items: b.items.filter((it) => !(it.productId === productId && it.variantLabel === variantLabel)) }));
  };

  const clearRoutineBasket = () => setRoutineBasket({ items: [] });

  useEffect(() => {
    const h = (e) => { if (!e.data) return; if (e.data.type === '__activate_edit_mode') setShowTweaks(true); if (e.data.type === '__deactivate_edit_mode') setShowTweaks(false); };
    window.addEventListener('message', h);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', h);
  }, []);

  const ALLOWED_WHILE_LOCKED = ['branch', 'product', 'cart', 'checkout', 'upi', 'order-confirm', 'routine-review', 'address-editor', 'write-review'];

  const navigate = useCallback((type, data = {}) => {
    if (supplierLock) {
      if (!ALLOWED_WHILE_LOCKED.includes(type)) return;
      if (type === 'branch' && data.branch?.id && data.branch.id !== supplierLock.branchId) return;
      if (type === 'product' && data.branch?.id && data.branch.id !== supplierLock.branchId) return;
    }
    setStack((s) => [...s, { type, data }]);
  }, [supplierLock]);

  const goBack = useCallback(() => {
    setStack((s) => {
      if (supplierLock && s.length <= 1) return s;
      return s.slice(0, -1);
    });
  }, [supplierLock]);

  const resetStack = useCallback(() => setStack([]), []);
  const top = stack.length > 0 ? stack[stack.length - 1] : null;

  // While locked, push the supplier's target onto an empty stack so the
  // customer can never see Home / category browse / other vendors.
  useEffect(() => {
    if (screen !== 'app' || !supplierLock || stack.length > 0) return;
    const branch = D.branches.grocery.find((b) => b.id === supplierLock.branchId) || D.branches.salons.find((b) => b.id === supplierLock.branchId);
    if (!branch) return;
    const t = supplierLock.target;
    if (t.kind === 'store') {
      setStack([{ type: 'branch', data: { branch, btype: D.branches.salons.some((b) => b.id === branch.id) ? 'salons' : 'grocery' } }]);
    } else if (t.kind === 'product') {
      const product = branch.products?.find((p) => p.id === t.productId);
      if (!product) return;
      setStack([{ type: 'product', data: { product, branch, openSubscribeOnMount: true, variantLabel: t.variantLabel } }]);
    } else if (t.kind === 'bundle') {
      const items = (t.items || []).map((it) => {
        const product = branch.products?.find((p) => p.id === it.productId);
        if (!product) return null;
        const variant = product.variants?.find((v) => v.l === it.variantLabel) || product.variants?.[0];
        return {
          productId: product.id,
          branchId: branch.id,
          branchName: branch.name,
          branchRating: branch.rating,
          name: product.name,
          em: product.em,
          cat: product.cat,
          variantLabel: variant?.l || '',
          pricePaise: variant?.p || 0,
          qty: it.qty || 1,
          cadence: it.cadence || 'Daily',
          customDays: it.customDays || null,
          weeklyDay: null,
          cadences: product.cadences || ['Daily', 'Alt-day', 'Weekly', 'Custom'],
        };
      }).filter(Boolean);
      setRoutineBasket({ items });
      setStack([{ type: 'routine-review', data: {} }]);
    }
  }, [screen, supplierLock, stack.length]);

  // Lock release on order-confirm: mark invite paid, clear lock + basket + URL.
  const handleOrderConfirmed = useCallback((orderId) => {
    if (supplierLock) {
      completeSupplierInvite(supplierLock.token, { orderId }).catch(() => {});
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
      setSupplierLock(null);
      setRoutineBasket({ items: [] });
    }
  }, [supplierLock]);

  if (inviteSpent) return (
    <div className="phone">
      <div className="scr">
        <InviteUsedScreen
          branchName={inviteSpent.branchName}
          status={inviteSpent.status}
          onContinue={() => {
            try { window.history.replaceState({}, '', window.location.pathname); } catch {}
            setInviteSpent(null);
            setScreen('app');
          }}
        />
      </div>
    </div>
  );

  if (screen === 'onboarding') return <div className="phone"><OnboardingScreen onDone={() => setScreen('phone')} /></div>;
  if (screen === 'phone')      return <div className="phone"><PhoneScreen onNext={(p) => { setPhone(p); setScreen('otp'); }} /></div>;
  if (screen === 'otp')        return <div className="phone"><OTPScreen phone={phone} onVerify={() => setScreen(supplierLock ? 'app' : 'location')} onBack={() => supplierLock ? null : setScreen('phone')} /></div>;
  if (screen === 'location')   return <div className="phone"><LocationScreen onSet={(loc) => { if (loc) setLocation(loc); setScreen('app'); }} /></div>;

  const renderTop = () => {
    if (!top) return null;
    const { type, data } = top;
    const gb = goBack;
    switch (type) {
      case 'category-feed':   return <CategoryFeedScreen category={data.cat} navigate={navigate} goBack={gb} cart={cart} setCart={setCart} />;
      case 'branch':          return <BranchScreen branch={data.branch} btype={data.btype} navigate={navigate} goBack={gb} cart={cart} setCart={setCart} addToRoutineBasket={addToRoutineBasket} routineBasket={routineBasket} />;
      case 'product':         return <ProductDetailScreen product={data.product} branch={data.branch} navigate={navigate} goBack={gb} cart={cart} setCart={setCart} addresses={addresses} setSubscriptions={setSubscriptions} openSubscribeOnMount={!!data.openSubscribeOnMount} initialVariantLabel={data.variantLabel} initialCadence={data.initialCadence} addToRoutineBasket={addToRoutineBasket} routineBasket={routineBasket} />;
      case 'product-catalog': return <ProductCatalogScreen navigate={navigate} goBack={gb} addToRoutineBasket={addToRoutineBasket} routineBasket={routineBasket} initialCategory={data.category} />;
      case 'routine-review':  return <RoutineReviewScreen basket={routineBasket} updateItem={updateBasketItem} removeItem={removeFromRoutineBasket} clearBasket={clearRoutineBasket} addresses={addresses} setSubscriptions={setSubscriptions} navigate={navigate} goBack={gb} resetStack={resetStack} setActiveTab={setActiveTab} />;
      case 'write-review':    return <WriteReviewScreen branchName={data.branchName} goBack={gb} onSubmit={() => goBack()} />;
      case 'cart':            return <CartScreen cart={cart} setCart={setCart} navigate={navigate} goBack={gb} addresses={addresses} />;
      case 'checkout':        return <CheckoutScreen cart={cart} setCart={setCart} navigate={navigate} goBack={gb} addresses={addresses} />;
      case 'upi':             return <UPIScreen amount={data.amount || cart.items.reduce((a, i) => a + i.priceP * i.qty, 0) * 1.05 + 3000} navigate={navigate} goBack={gb} onSuccess={() => { setCart({ branchId: null, branchName: '', items: [] }); resetStack(); navigate('order-confirm', { orderId: 'ORD-' + Math.floor(1000 + Math.random() * 9000) }); }} />;
      case 'order-confirm':   return <OrderConfirmScreen orderId={data.orderId || 'ORD-2403'} navigate={navigate} resetStack={resetStack} supplierLock={supplierLock} onOrderConfirmed={handleOrderConfirmed} setActiveTab={setActiveTab} />;
      case 'booking-service': return <BookingServiceScreen service={data.service} branch={data.branch} navigate={navigate} goBack={gb} />;
      case 'booking-flow':    return <BookingFlowScreen service={data.service} branch={data.branch} navigate={navigate} goBack={gb} />;
      case 'booking-detail':  return <BookingDetailScreen booking={data.booking} goBack={gb} />;
      case 'order-detail':    return <OrderDetailScreen order={data.order} goBack={gb} />;
      case 'sub-detail':      return <SubDetailScreen sub={data.sub} goBack={gb} navigate={navigate} />;
      case 'address-editor':  return <AddressEditorScreen address={data.address} onSave={(addr) => { setAddresses((a) => data.address ? a.map((x) => x.id === data.address.id ? { ...addr, id: x.id } : x) : [...a, { ...addr, id: 'a' + Date.now() }]); gb(); }} goBack={gb} />;
      case 'notifications':   return <NotificationsScreen goBack={gb} navigate={navigate} />;
      case 'search':          return <SearchScreen goBack={gb} navigate={navigate} initialCategory={data.initialCategory} />;
      case 'vendor-dashboard': return <VendorDashboard goBack={gb} />;
      default:                 return <NotFoundScreen onHome={resetStack} />;
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'home':          return <HomeScreen navigate={navigate} cart={cart} setCart={setCart} subscriptions={subscriptions} onTabChange={setActiveTab} location={location} addresses={addresses} setAddresses={setAddresses} />;
      case 'orders':        return <OrdersScreen navigate={navigate} />;
      case 'subscriptions': return <SubscriptionsScreen navigate={navigate} subs={subscriptions} setSubscriptions={setSubscriptions} />;
      case 'account':       return <AccountScreen navigate={navigate} addresses={addresses} setAddresses={setAddresses} />;
      default:              return <HomeScreen navigate={navigate} cart={cart} setCart={setCart} subscriptions={subscriptions} onTabChange={setActiveTab} location={location} addresses={addresses} setAddresses={setAddresses} />;
    }
  };

  const isUPI = top?.type === 'upi';
  const showBasketPill = !supplierLock
    && routineBasket.items.length > 0
    && !['routine-review', 'upi', 'order-confirm', 'checkout', 'cart'].includes(top?.type);
  return (
    <div className="phone" style={{ position: 'relative', background: isUPI ? '#1c1917' : undefined }}>
      <OfflineBanner />
      {supplierLock && top?.type !== 'order-confirm' && (
        <SupplierLockBanner branchName={supplierLock.branchName} onInfo={() => setShowLockInfo(true)} />
      )}
      <div className="scr" style={supplierLock && top?.type !== 'order-confirm' ? { paddingTop: 36 } : undefined} key={top ? top.type + (top.data?.branch?.id || top.data?.order?.id || top.data?.booking?.id || top.data?.sub?.id || '') : activeTab}>
        {top ? renderTop() : renderTab()}
      </div>
      {showBasketPill && (
        <RoutineBasketPill count={routineBasket.items.length} onOpen={() => navigate('routine-review')} aboveNav={!top} />
      )}
      {!top && !supplierLock && <BNav tab={activeTab} onTab={(t) => { setActiveTab(t); resetStack(); }} />}

      {showInstall && <InstallPwaModal onClose={() => setShowInstall(false)} />}

      {showLockInfo && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setShowLockInfo(false)}>
          <div style={{ background: C.white, borderRadius: 18, padding: 22, maxWidth: 320 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>📬</div>
            <p style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 8 }}>You're shopping by invite</p>
            <p style={{ fontSize: 13, color: C.textMed, lineHeight: 1.55, marginBottom: 16 }}>
              <b style={{ color: C.text }}>{supplierLock?.branchName}</b> sent you this link. Complete this purchase to unlock the rest of Rekraft and start exploring other suppliers and subscriptions.
            </p>
            <button onClick={() => setShowLockInfo(false)} style={{ width: '100%', padding: '11px 16px', borderRadius: 12, border: 'none', background: C.primary, color: C.white, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Got it</button>
          </div>
        </div>
      )}

      {showTweaks && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }} onClick={() => { setShowTweaks(false); window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*'); }}>
          <div style={{ background: C.white, borderRadius: '24px 24px 0 0', width: '100%', padding: '12px 20px 32px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><div style={{ width: 40, height: 4, borderRadius: 2, background: C.border }} /></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}><p style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Tweaks</p><button onClick={() => { setShowTweaks(false); window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*'); }} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: 16 }}>×</button></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
              <div><p style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Skip auth (start at home)</p><p style={{ fontSize: 11, color: C.textMed }}>Reload to apply</p></div>
              <Toggle val={screen === 'app'} onChange={(v) => { if (v) setScreen('app'); else setScreen('phone'); }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
